/**
 * File Uploader Admin API — V2 Domain
 *
 * All functions call the real /file-upload/ backend endpoints.
 * Schema and ColumnCheck objects are derived from the nested FileTemplate
 * response, since the backend manages them as part of the template aggregate.
 */

import { getCsrfToken } from "utils/csrf";

const API = "/file-upload";

// ---------------------------------------------------------------------------
// HTTP helper
// ---------------------------------------------------------------------------

async function apiRequest(url, options = {}) {
  const isWrite = options.method && options.method !== "GET";
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(isWrite ? { "X-CSRFToken": getCsrfToken() } : {}),
      ...options.headers,
    },
    ...options,
  });

  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType && !contentType.includes("application/json")) {
    // Non-JSON response — grab a preview of the body to aid debugging
    let preview = "";
    try {
      const text = await response.text();
      // Strip HTML tags for readability
      preview = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 400);
    } catch { /* ignore */ }
    throw new Error(
      `Server returned non-JSON (${response.status} ${response.statusText}).` +
        (preview ? ` Server says: ${preview}` : " Check that backend migrations are applied and the Django dev server is running.")
    );
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error(`Server error: ${response.status} ${response.statusText}`);
  }

  if (!response.ok) {
    // Surface the most useful part of a DRF validation error
    const message =
      data?.detail ||
      data?.error ||
      data?.message ||
      (typeof data === "object" ? JSON.stringify(data) : null) ||
      `Request failed (${response.status})`;
    throw new Error(message);
  }

  return data;
}

// ---------------------------------------------------------------------------
// Normalizers — backend shape → frontend shape
// ---------------------------------------------------------------------------

function normalizeColumnCheck(cc) {
  return {
    _id: cc.id,
    check_type: cc.check_type,
    values: cc.values ?? [],
    user_emails: cc.user_emails ?? [],
    apply_to: (cc.user_emails ?? []).length === 0 ? "all" : "specific",
  };
}

function normalizeColumn(col) {
  return {
    id: col.id,
    name: col.name,
    position: col.position,
    data_type: col.data_type,
    is_required: col.is_required,
    allow_missing: col.allow_missing,
    column_checks: (col.column_checks ?? []).map(normalizeColumnCheck),
  };
}

function normalizeTemplate(tmpl) {
  const fileTypes = tmpl.file_types ?? [];
  return {
    id: tmpl.id,
    name: tmpl.name,
    description: tmpl.description ?? "",
    partner_id: tmpl.partner_id,
    storage_location_uri: tmpl.storage_location_uri,
    storage_location_type: tmpl.storage_location_type,
    dataset_name: tmpl.dataset_name ?? "pad_file_uploader",
    overwrite_existing: tmpl.overwrite_existing,
    has_uploaded_files: (tmpl.files ?? []).length > 0,
    schema_id: tmpl.file_schema?.id ?? null,
    schema_name: tmpl.file_schema?.name ?? "",
    schema_strict: tmpl.file_schema?.strict ?? false,
    columns: (tmpl.file_schema?.columns ?? []).map(normalizeColumn),
    file_type_rows: fileTypes.map((ft) => ({
      _rowId: ft.id,
      _fileTypeId: ft.id,
      extension: ft.file_extension,
      encoding: ft.file_encoding,
      skip_rows: ft.skip_rows ?? 0,
      delimiter: ft.delimiter ?? "",
    })),
    created_at: tmpl.created_at,
    updated_at: tmpl.updated_at,
  };
}

// ---------------------------------------------------------------------------
// FileTemplate CRUD
// ---------------------------------------------------------------------------

export async function fetchTemplates(projectId) {
  const params = projectId ? `?project_id=${encodeURIComponent(projectId)}` : "";
  const data = await apiRequest(`${API}/file-templates/${params}`);
  const results = Array.isArray(data?.results) ? data.results : data;
  return results.map(normalizeTemplate);
}

export async function fetchTemplate(id) {
  const data = await apiRequest(`${API}/file-templates/${id}/`);
  return normalizeTemplate(data);
}

export async function deleteTemplate(id) {
  return apiRequest(`${API}/file-templates/${id}/`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// FileType resolution — reuse existing or create new
// ---------------------------------------------------------------------------

async function fetchFileTypes() {
  const data = await apiRequest(`${API}/file-types/`);
  return Array.isArray(data?.results) ? data.results : (data ?? []);
}

/**
 * For each file type row, find an existing FileType with identical settings
 * for this partner or create a new one.
 * Returns an array of FileType UUIDs to pass in the template payload.
 * Multiple rows with the same extension (but different encoding/skip_rows/delimiter)
 * each resolve to a distinct FileType object.
 */
async function resolveFileTypeIds(fileTypeRows, partnerId) {
  const existing = await fetchFileTypes();
  const ids = [];

  for (const row of fileTypeRows) {
    const encoding = row.encoding || "utf-8";
    const skipRows = row.skip_rows ?? 0;
    const delimiter = row.delimiter || null;

    const match = existing.find(
      (ft) =>
        ft.file_extension === row.extension &&
        ft.file_encoding === encoding &&
        (ft.skip_rows ?? 0) === skipRows &&
        (ft.delimiter || null) === delimiter
    );

    if (match) {
      ids.push(match.id);
    } else {
      const name = `${row.extension.replace(".", "").toUpperCase()}-${Date.now()}`;
      const created = await apiRequest(`${API}/file-types/`, {
        method: "POST",
        body: JSON.stringify({
          partner_id: partnerId,
          name,
          file_extension: row.extension,
          file_encoding: encoding,
          skip_rows: skipRows,
          delimiter,
        }),
      });
      ids.push(created.id);
    }
  }

  return ids;
}

// ---------------------------------------------------------------------------
// Template save — resolves file types then POST/PUT
// ---------------------------------------------------------------------------

// Column IDs that don't match a UUID are client-generated; send null so the backend creates a new record.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function buildColumnPayload(col) {
  return {
    id: UUID_RE.test(String(col.id ?? "")) ? col.id : null,
    name: col.name,
    position: col.position,
    data_type: col.data_type,
    is_required: col.is_required,
    allow_missing: col.allow_missing ?? !col.is_required,
    column_checks: (col.column_checks ?? []).map((cc) => ({
      check_type: cc.check_type,
      values: cc.values ?? [],
      user_emails: cc.user_emails ?? [],
    })),
  };
}

export async function saveTemplate({ isEdit, templateId, form, columns, partnerId }) {
  const fileTypeIds = await resolveFileTypeIds(form.file_type_rows, partnerId);

  const payload = {
    partner_id: partnerId,
    name: form.name,
    description: form.description || null,
    schema_name: form.schema_name,
    schema_strict: form.schema_strict ?? false,
    storage_location_uri: form.storage_location_uri,
    storage_location_type: form.storage_location_type,
    dataset_name: form.dataset_name || "pad_file_uploader",
    overwrite_existing: form.overwrite_existing ?? false,
    file_type_ids: fileTypeIds,
    columns: columns.map(buildColumnPayload),
  };

  if (isEdit) {
    return apiRequest(`${API}/file-templates/${templateId}/`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }
  return apiRequest(`${API}/file-templates/`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ---------------------------------------------------------------------------
// Schemas — derived from templates (1:1 relationship)
// ---------------------------------------------------------------------------

export async function fetchSchemas(projectId) {
  const templates = await fetchTemplates(projectId);
  const seen = new Set();
  return templates
    .filter((t) => t.schema_name && !seen.has(t.schema_id) && seen.add(t.schema_id))
    .map((t) => ({
      id: t.schema_id,
      name: t.schema_name,
      strict: t.schema_strict,
      columns: t.columns,
      template_id: t.id,
      template_name: t.name,
      created_at: t.created_at,
      templates_using: templates
        .filter((tt) => tt.schema_id === t.schema_id)
        .map((tt) => ({ id: tt.id, name: tt.name })),
    }));
}

export async function fetchSchema(templateId) {
  const tmpl = await fetchTemplate(templateId);
  return {
    id: tmpl.schema_id,
    name: tmpl.schema_name,
    strict: tmpl.schema_strict,
    columns: tmpl.columns,
  };
}

// ---------------------------------------------------------------------------
// Column Checks — derived from templates
// ---------------------------------------------------------------------------

export async function fetchColumnChecks(projectId) {
  const templates = await fetchTemplates(projectId);
  return templates.flatMap((t) =>
    t.columns.flatMap((col) =>
      (col.column_checks ?? []).map((cc) => ({
        ...cc,
        column_name: col.name,
        template_id: t.id,
        template_name: t.name,
        schema_name: t.schema_name,
      }))
    )
  );
}

// ---------------------------------------------------------------------------
// Schema inference — POST /file-upload/infer-schema/
// Accepts CSV, TSV, TXT, and SAV files. The file is never stored server-side.
// ---------------------------------------------------------------------------

export async function inferSchemaFromFile(file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await fetch(`${API}/infer-schema/`, {
    method: "POST",
    credentials: "include",
    headers: { "X-CSRFToken": getCsrfToken() },
    body: formData,
  });

  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("application/json")) {
    let preview = "";
    try {
      const text = await response.text();
      preview = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 400);
    } catch { /* ignore */ }
    throw new Error(
      `Server returned non-JSON (${response.status} ${response.statusText}).` +
        (preview ? ` Server says: ${preview}` : "")
    );
  }

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.error || `Request failed (${response.status})`);
  }
  return data;
}
